import React, { useEffect, useMemo, useCallback } from "react";
import Moment from "react-moment";
import UploadDocPopup from "../UploadDocPopup";
import { useDispatch, useSelector } from "react-redux";
import {
  useLoadCandidateAppsQuery,
  useLoadAppRuningTasksQuery,
  useLoadAppDocumentsQuery,
  useLoadInvoiceDocumentsQuery,
  useLoadReqDocumentsQuery,
} from "../../app/store/candidateApiSlice";
import { getCurrentUser, getLoggedUserDetails, loadUserDetails } from "../../app/store/auth";
import { getAlert } from "../../Services/appAlerts";
import { setCurrentPageTitle, setCurrentRoute } from "../../app/store/layoutUI";
import useDocDownload from "../../app/hooks/useDocDownload";
import {
  getComponentUserStatus,
  getToolsCount,
  toolsCountReceived,
  UpdateComponentsUserStatus,
} from "../../app/store/tools";
import { useState } from "react";

const SENT_DOCS_STYLES = {
  pageWrap: {
    padding: "8px 0 4px 0"
  },
  shell: {
    borderRadius: "0px",
    border: "1px solid #e5e5e5",
    backgroundColor: "#ffffff",
    boxShadow: "0 10px 30px rgba(15, 23, 42, 0.05)",
    overflow: "hidden"
  },
  hero: {
    padding: "14px 20px",
    borderBottom: "1px solid #e5e5e5",
    background: "#ffffff"
  },
  heroRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "16px",
    flexWrap: "wrap"
  },
  titleWrap: {
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    gap: "12px"
  },
  titleIcon: {
    width: "32px",
    height: "32px",
    borderRadius: "0px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#ffffff",
    color: "#000000",
    fontSize: "14px",
    border: "1px solid #e5e5e5",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
    flexShrink: 0
  },
  titleCopy: {
    minWidth: 0
  },
  title: {
    fontSize: "16px",
    fontWeight: 600,
    lineHeight: 1.25,
    color: "#000000",
    margin: 0
  },
  subtitle: {
    fontSize: "13px",
    lineHeight: 1.5,
    color: "#666666",
    margin: 0
  },
  refreshButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    borderRadius: "0px",
    border: "1px solid #e5e5e5",
    background: "#ffffff",
    color: "#333333",
    padding: "8px 16px",
    fontSize: "13px",
    fontWeight: 600,
    boxShadow: "0 2px 6px rgba(15, 23, 42, 0.04)",
    cursor: "pointer",
    transition: "all 0.2s ease"
  },
  uploadButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    borderRadius: "0px",
    border: "1px solid #2563eb",
    background: "#2563eb",
    color: "#ffffff",
    padding: "9px 18px",
    fontSize: "13px",
    fontWeight: 600,
    boxShadow: "0 2px 6px rgba(15, 23, 42, 0.2)",
    cursor: "pointer",
    transition: "all 0.2s ease"
  },
  statsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "14px",
    padding: "20px 22px"
  },
  statCard: {
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
    backgroundColor: "#ffffff",
    border: "1px solid rgba(226, 232, 240, 0.95)",
    borderRadius: "0px",
    padding: "16px",
    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.04)",
    transition: "all 0.2s ease"
  },
  statIcon: {
    width: "36px",
    height: "36px",
    borderRadius: "0px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f1f5f9",
    color: "#0f172a",
    fontSize: "14px",
    flexShrink: 0
  },
  statContent: {
    minWidth: 0
  },
  statLabel: {
    margin: "0 0 4px 0",
    color: "#64748b",
    fontSize: "11px",
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase"
  },
  statValue: {
    margin: 0,
    color: "#0f172a",
    fontSize: "24px",
    fontWeight: 600,
    lineHeight: 1
  },
  body: {
    padding: "0 22px 22px 22px",
    display: "grid",
    gap: "20px"
  },
  sectionCard: {
    borderRadius: "0px",
    border: "1px solid #e5e5e5",
    background: "#ffffff",
    overflow: "hidden"
  },
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    padding: "14px 18px",
    borderBottom: "1px solid #e5e5e5",
    background: "#ffffff"
  },
  sectionTitleWrap: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    minWidth: 0
  },
  sectionIcon: {
    width: "32px",
    height: "32px",
    borderRadius: "0px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f1f5f9",
    color: "#0f172a",
    fontSize: "14px",
    border: "1px solid #e2e8f0"
  },
  sectionTitle: {
    fontSize: "15px",
    fontWeight: 600,
    color: "#0f172a"
  },
  sectionMeta: {
    fontSize: "13px",
    lineHeight: 1.5,
    color: "#666666",
    margin: 0,
    fontWeight: "normal"
  },
  sectionBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "28px",
    height: "28px",
    padding: "0 10px",
    borderRadius: "0px",
    background: "#f1f5f9",
    border: "1px solid #e2e8f0",
    color: "#0f172a",
    fontSize: "12px",
    fontWeight: 600
  },
  table: {
    width: "100%",
    marginBottom: 0,
    borderCollapse: "separate",
    borderSpacing: 0,
    fontSize: "13px",
    color: "#334155"
  },
  tableHeadCell: {
    borderTop: "none",
    fontSize: "11px",
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#64748b",
    background: "#f8fafc",
    borderBottom: "1px solid #e5e5e5",
    padding: "14px 16px"
  },
  cell: {
    padding: "14px 16px",
    borderBottom: "1px solid rgba(241, 245, 249, 1)",
    verticalAlign: "top",
    fontWeight: 600
  },
  dateText: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "12.5px",
    fontWeight: 600,
    color: "#0f172a"
  },
  fileLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    borderRadius: "0px",
    border: "none",
    background: "transparent",
    color: "#2563eb",
    padding: "0",
    fontSize: "13px",
    fontWeight: 600,
    boxShadow: "none",
    cursor: "pointer",
    transition: "all 0.2s ease",
    textAlign: "left",
    textDecoration: "none"
  },
  fileSubtext: {
    fontSize: "11.5px",
    color: "#64748b",
    marginTop: "4px",
    fontWeight: 600
  },
  esignBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    width: "fit-content",
    marginTop: "6px",
    padding: "3px 8px",
    borderRadius: "0px",
    backgroundColor: "#eff6ff",
    border: "1px solid #bfdbfe",
    color: "#1d4ed8",
    fontSize: "11.5px",
    fontWeight: 700,
    lineHeight: 1.2
  },
  typeBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    padding: "4px 10px",
    borderRadius: "0px",
    backgroundColor: "#f1f5f9",
    border: "1px solid #e2e8f0",
    color: "#0f172a",
    fontSize: "12px",
    fontWeight: 600
  },
  notesText: {
    whiteSpace: "pre-wrap",
    fontSize: "12.5px",
    lineHeight: 1.5,
    color: "#475569",
    fontWeight: 600
  },
  emptyState: {
    padding: "42px 24px",
    textAlign: "center",
    backgroundColor: "#ffffff",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center"
  },
  emptyIcon: {
    width: "64px",
    height: "64px",
    margin: "0 auto 14px auto",
    borderRadius: "0px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f1f5f9",
    color: "#0f172a",
    fontSize: "26px"
  },
  emptyTitle: {
    margin: 0,
    color: "#0f172a",
    fontSize: "18px",
    fontWeight: 600,
    textAlign: "center"
  },
  emptyText: {
    fontSize: "13px",
    lineHeight: 1.5,
    color: "#666666",
    margin: "10px auto 0 auto",
    fontWeight: "normal"
  }
};

const ROUTE_CONFIG_PARTNER = [{ name: "Contract Management", url: "" }];

export default function SentDocs() {
  const [uploadDocShowHide, setUploadDocShowHide] = useState(false);

  const [wfTask, setWfTask] = useState(null);

  const userDetailsSelector = useSelector(getLoggedUserDetails);
  const toolsCountSelector = useSelector(getToolsCount);
  const componentUserStatusSelector = useSelector(getComponentUserStatus);

  const dispatch = useDispatch();
  const currentUser = getCurrentUser();
  const currentUserId = currentUser?.linkUser || currentUser?.id;
  const currentUserIdRaw = currentUser?.id;

  const partnerId = (userDetailsSelector?.approved > 0) ? userDetailsSelector?.partnerid : null;

  // RTK Query: Declarative Loading of Candidate Apps
  const {
    data: candidateApps = [],
    isLoading: isAppsLoading,
    isFetching: isAppsFetching,
    refetch: refetchCandidateApps
  } = useLoadCandidateAppsQuery(currentUserId, { skip: !currentUserId });

  const workflow = useMemo(() => {
    if (candidateApps.length > 0) {
      const mainWorkflow = candidateApps.filter(x => x.workflowType === 1);
      return mainWorkflow.length > 0 ? mainWorkflow[0] : null;
    }
    return null;
  }, [candidateApps]);

  const {
    data: appTasksSelector = [],
    isLoading: isTasksLoading,
    isFetching: isTasksFetching,
    refetch: refetchAppTasks
  } = useLoadAppRuningTasksQuery(
    { wfId: workflow?.oldWorkflowId, userId: currentUserId },
    { skip: !workflow || !currentUserId }
  );

  const appTasklist = useMemo(() => {
    return appTasksSelector.filter(x => x.taskType === 1 && x.wiid > 0);
  }, [appTasksSelector]);

  const {
    data: appDocuments = [],
    isLoading: isDocsLoading,
    isFetching: isDocsFetching,
    refetch: refetchAppDocuments
  } = useLoadAppDocumentsQuery(
    { wfId: workflow?.oldWorkflowId, userId: currentUserId },
    { skip: !workflow || !currentUserId }
  );

  const {
    data: invoiceDocuments = [],
    isLoading: isInvoiceLoading,
    isFetching: isInvoiceFetching,
    refetch: refetchInvoiceDocs
  } = useLoadInvoiceDocumentsQuery(partnerId, { skip: !partnerId });

  const {
    data: reqDocuments = [],
    isLoading: isReqDocsLoading,
    isFetching: isReqDocsFetching,
    refetch: refetchReqDocs
  } = useLoadReqDocumentsQuery(partnerId, { skip: !partnerId });

  const sentDocs = useMemo(() => {
    if (!appDocuments) return [];
    return appDocuments.filter(x => x.type === "send");
  }, [appDocuments]);

  const receivedDocs = useMemo(() => {
    let recList = [];
    if (appDocuments.length > 0) {
      const docs = appDocuments.filter(x => x.type === "received" && x.sendStatus === true);
      recList = [...recList, ...docs];
    }
    if (invoiceDocuments.length > 0) {
      recList = [...recList, ...invoiceDocuments];
    }
    if (reqDocuments.length > 0) {
      recList = [...recList, ...reqDocuments];
    }
    return recList;
  }, [appDocuments, invoiceDocuments, reqDocuments]);

  const isInitialLoading = isAppsLoading || isTasksLoading || isDocsLoading || isInvoiceLoading || isReqDocsLoading || (!workflow && candidateApps.length === 0);
  const isDataFetching = isAppsFetching || isTasksFetching || isDocsFetching || isInvoiceFetching || isReqDocsFetching;
  const isDataLoading = isInitialLoading || (isDataFetching && (sentDocs.length + receivedDocs.length) === 0);

  const handleUpdateComponent = useCallback((compItems, ciid) => {
    const comp = componentUserStatusSelector.filter(x => x.compId === 2);
    let updCompItems = "";
    if (comp.length > 0) {
      updCompItems = comp[0].compItems;
    }
    const compObj = {
      compName: "Document History",
      performerId: currentUserIdRaw,
      compItems: compItems,
      ciid: ciid,
      wiid: 0,
      iiid: 0,
      compId: 2
    }
    if (compItems !== "" && compItems !== updCompItems) {
      dispatch(UpdateComponentsUserStatus(compObj));
    }
  }, [componentUserStatusSelector, currentUserIdRaw, dispatch]);

  useEffect(() => {
    dispatch(setCurrentPageTitle("Document History"));
    dispatch(setCurrentRoute(ROUTE_CONFIG_PARTNER));
  }, [dispatch]);

  useEffect(() => {
    const displayedDocs = [...sentDocs, ...receivedDocs];
    const docItems = [...new Set(
      displayedDocs
        .map((doc) => doc.id)
        .filter((id) => id != null)
        .map(String)
    )].sort().join(",");
    const targetCiid = displayedDocs.find((doc) => doc.ciid > 0)?.ciid;

    if (docItems && targetCiid > 0) {
      handleUpdateComponent(docItems, targetCiid);
    }
  }, [sentDocs, receivedDocs, handleUpdateComponent]);

  const handleRefreshSentDocs = useCallback(() => {
    let targetUsrId = currentUser?.linkUser || currentUser?.id;
    if (targetUsrId) {
      dispatch(loadUserDetails(targetUsrId));
    }
    if (refetchCandidateApps) refetchCandidateApps();
    if (refetchAppTasks) refetchAppTasks();
    if (refetchAppDocuments) refetchAppDocuments();
    if (refetchInvoiceDocs) refetchInvoiceDocs();
    if (refetchReqDocs) refetchReqDocs();
  }, [currentUser, dispatch, refetchCandidateApps, refetchAppTasks, refetchAppDocuments, refetchInvoiceDocs, refetchReqDocs]);

  const [handleDownloadFile, downloadingDocId] = useDocDownload();

  const handleUploadDocModalShowHide = useCallback((app) => {
    if (app === "save") {
      setUploadDocShowHide(false);
      refetchAppDocuments();
    }
    else {
      const reviewApp = appTasklist.filter((x) => x.registrationType === "WP");
      if (reviewApp.length > 0) {
        const lastReviewer = reviewApp[0];
        setWfTask([lastReviewer]);
      } else {
        getAlert("", "No reviewer found", "error");
      }
      setUploadDocShowHide(prev => !prev);
    }
  }, [appTasklist, refetchAppDocuments]);

  const getDocType = useCallback((type) => {
    const normalizedType = String(type || "").toLowerCase();

    if (!normalizedType)
      return "Uploaded";
    else if (normalizedType.includes("weekly_time_sheet"))
      return "Weekly Time Sheet";
    else if (normalizedType.includes("monthly_time_sheet"))
      return "Monthly Time Sheet";
    else if (type === "sharing")
      return "Contract";
    else if (type?.substring(0, 3) === "ach")
      return "ACH Void Check";
    else if (type?.substring(0, 9) === "insurance")
      return "COI Insurance";
    else if (type?.substring(0, 3) === "req" || type?.includes("_c2c_"))
      return "Required";
    else return type;
  }, []);

  const totalDocuments = sentDocs.length + receivedDocs.length;
  const totalSentDocuments = sentDocs.length;
  const totalReceivedDocuments = receivedDocs.length;
  useEffect(() => {
    if (!Array.isArray(toolsCountSelector)) return;

    const historyCount = toolsCountSelector.find((item) => item.key === 2);
    if (!historyCount || historyCount.value === totalDocuments) return;

    dispatch(toolsCountReceived(
      toolsCountSelector.map((item) =>
        item.key === 2 ? { ...item, value: totalDocuments } : item
      )
    ));
  }, [dispatch, toolsCountSelector, totalDocuments]);

  return (
    <React.Fragment>
      <style>{`
        .doc-link-btn {
          border: none !important;
          background: transparent !important;
          box-shadow: none !important;
          padding: 0 !important;
          color: #2563eb !important;
        }
        .doc-link-btn:hover {
          text-decoration: underline !important;
          color: #1d4ed8 !important;
          background: transparent !important;
          box-shadow: none !important;
        }
        .doc-link-btn:focus,
        .doc-link-btn:active {
          box-shadow: none !important;
          background: transparent !important;
        }
      `}</style>
      <div className="col-md-12" style={SENT_DOCS_STYLES.pageWrap}>
        <div style={SENT_DOCS_STYLES.shell}>
          <div style={SENT_DOCS_STYLES.hero}>
            <div style={SENT_DOCS_STYLES.heroRow}>
              <div style={SENT_DOCS_STYLES.titleWrap}>
                <div style={SENT_DOCS_STYLES.titleIcon}>
                  <i className="fa fa-folder-open-o" aria-hidden="true"></i>
                </div>
                <div style={SENT_DOCS_STYLES.titleCopy}>
                  <h1 style={SENT_DOCS_STYLES.title}>Document History</h1>
                  <p style={SENT_DOCS_STYLES.subtitle}>Review all sent and received documents across your workflow.</p>
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                <button
                  type="button"
                  className="btn btn-sm"
                  style={SENT_DOCS_STYLES.refreshButton}
                  onClick={handleRefreshSentDocs}
                  disabled={isDataFetching}
                  title="Refresh Document History"
                >
                  <i className={`fa fa-refresh ${isDataFetching ? "fa-spin" : ""}`} aria-hidden="true"></i>{" "}
                  {isDataFetching ? "Fetching..." : "Refresh"}
                </button>
              </div>
            </div>
          </div>

          {isDataFetching && (sentDocs.length > 0 || receivedDocs.length > 0) && (
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "8px 18px",
              background: "#eff6ff",
              borderTop: "1px solid #bfdbfe",
              borderBottom: "1px solid #bfdbfe",
              color: "#1e40af",
              fontSize: "12px",
              fontWeight: 600
            }}>
              <div style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                <i className="fa fa-circle-o-notch fa-spin" style={{ color: "#2563eb", fontSize: "13px" }}></i>
                <span>Fetching updated documents...</span>
              </div>
              <span style={{ fontSize: "11px", color: "#3b82f6", fontWeight: 500 }}>Updating</span>
            </div>
          )}

          <div style={SENT_DOCS_STYLES.statsRow}>
            <div style={SENT_DOCS_STYLES.statCard}>
              <div style={SENT_DOCS_STYLES.statIcon}>
                <i className="fa fa-files-o" aria-hidden="true"></i>
              </div>
              <div style={SENT_DOCS_STYLES.statContent}>
                <div style={SENT_DOCS_STYLES.statLabel}>Total Documents</div>
                <div style={SENT_DOCS_STYLES.statValue}>
                  {isDataLoading ? <i className="fa fa-circle-o-notch fa-spin" style={{ fontSize: "16px", color: "#64748b" }}></i> : totalDocuments}
                </div>
              </div>
            </div>
            <div style={SENT_DOCS_STYLES.statCard}>
              <div style={SENT_DOCS_STYLES.statIcon}>
                <i className="fa fa-upload" aria-hidden="true"></i>
              </div>
              <div style={SENT_DOCS_STYLES.statContent}>
                <div style={SENT_DOCS_STYLES.statLabel}>Sent</div>
                <div style={SENT_DOCS_STYLES.statValue}>
                  {isDataLoading ? <i className="fa fa-circle-o-notch fa-spin" style={{ fontSize: "16px", color: "#64748b" }}></i> : totalSentDocuments}
                </div>
              </div>
            </div>
            <div style={SENT_DOCS_STYLES.statCard}>
              <div style={SENT_DOCS_STYLES.statIcon}>
                <i className="fa fa-download" aria-hidden="true"></i>
              </div>
              <div style={SENT_DOCS_STYLES.statContent}>
                <div style={SENT_DOCS_STYLES.statLabel}>Received</div>
                <div style={SENT_DOCS_STYLES.statValue}>
                  {isDataLoading ? <i className="fa fa-circle-o-notch fa-spin" style={{ fontSize: "16px", color: "#64748b" }}></i> : totalReceivedDocuments}
                </div>
              </div>
            </div>
          </div>
          <div style={SENT_DOCS_STYLES.body}>
            <div style={SENT_DOCS_STYLES.sectionCard}>
              <div style={SENT_DOCS_STYLES.sectionHeader}>
                <div style={SENT_DOCS_STYLES.sectionTitleWrap}>
                  <div style={SENT_DOCS_STYLES.sectionIcon}>
                    <i className="fa fa-upload" aria-hidden="true"></i>
                  </div>
                  <div>
                    <div style={SENT_DOCS_STYLES.sectionTitle}>Sent Documents</div>
                    <div style={SENT_DOCS_STYLES.sectionMeta}>Files sent out from your workflow.</div>
                  </div>
                </div>
                <div style={SENT_DOCS_STYLES.sectionBadge}>{sentDocs.length}</div>
              </div>
              <div className="table-responsive">
                <table className="table align-items-center table-hover mb-0" style={SENT_DOCS_STYLES.table}>
                  <thead>
                    <tr>
                      <th style={SENT_DOCS_STYLES.tableHeadCell}>Date & Time Sent</th>
                      <th style={SENT_DOCS_STYLES.tableHeadCell}>Name</th>
                      <th style={SENT_DOCS_STYLES.tableHeadCell}>Type</th>
                      <th style={SENT_DOCS_STYLES.tableHeadCell}>Size</th>
                      <th style={SENT_DOCS_STYLES.tableHeadCell}>Notes</th>
                    </tr>
                  </thead>
                  <tbody style={{ opacity: isDataFetching && sentDocs.length > 0 ? 0.7 : 1, transition: "opacity 0.2s ease" }}>
                    {isDataLoading ? (
                      <tr>
                        <td colSpan="5" style={{ padding: "48px 20px", textAlign: "center", backgroundColor: "#ffffff" }}>
                          <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: "10px" }}>
                            <div style={{ width: "40px", height: "40px", background: "#f5f5f5", display: "flex", alignItems: "center", justifyContent: "center", color: "#000000", fontSize: "16px" }}>
                              <i className="fa fa-circle-o-notch fa-spin"></i>
                            </div>
                            <div style={{ fontSize: "14px", fontWeight: 600, color: "#0f172a" }}>
                              {isDataFetching ? "Fetching sent documents..." : "Loading sent documents..."}
                            </div>
                            <div style={{ fontSize: "12px", fontWeight: 500, color: "#64748b" }}>
                              Please wait while we retrieve your sent files.
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : sentDocs.length === 0 ? (
                      <tr>
                        <td colSpan="5" style={{ ...SENT_DOCS_STYLES.cell, padding: 0 }}>
                          <div style={SENT_DOCS_STYLES.emptyState}>
                            <div style={SENT_DOCS_STYLES.emptyIcon}>
                              <i className="fa fa-upload" aria-hidden="true"></i>
                            </div>
                            <h4 style={SENT_DOCS_STYLES.emptyTitle}>No sent documents yet</h4>
                            <p style={SENT_DOCS_STYLES.emptyText}>
                              Documents you send through the workflow will appear here for quick download and review.
                            </p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      sentDocs.map((item, key) => (
                        <tr key={item.id || `sent-${key}`}>
                          <td style={SENT_DOCS_STYLES.cell}>
                            <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                              <span style={{ fontSize: "13.5px", fontWeight: 600, color: "#0f172a" }}>
                                <Moment format="MMM Do, YYYY">
                                  {item.createdDateTime}
                                </Moment>
                              </span>
                              <span style={{ fontSize: "12px", fontWeight: 600, color: "#64748b" }}>
                                <Moment format="h:mm a">
                                  {item.createdDateTime}
                                </Moment>
                              </span>
                            </div>
                          </td>
                          <td style={SENT_DOCS_STYLES.cell}>
                            <button
                              className="btn btn-sm p-0 doc-link-btn"
                              style={SENT_DOCS_STYLES.fileLink}
                              title="Download Document"
                              disabled={downloadingDocId === (item.docId || item.id)}
                              onClick={() => handleDownloadFile(item)}
                            >
                              {downloadingDocId === (item.docId || item.id) ? (
                                <i className="fa fa-refresh fa-spin mr-1" aria-hidden="true"></i>
                              ) : (
                                <i className="fa fa-file-text-o mr-1" aria-hidden="true"></i>
                              )}
                              {(item.docId || item.id) ? `${item.docId || item.id} - ` : ""}{item.documentName}
                            </button>
                            {item.docModifiedName && (
                              String(item.docModifiedName).toLowerCase() === "esign" ? (
                                <span style={SENT_DOCS_STYLES.esignBadge}>
                                  <i className="fa fa-pencil-square-o" aria-hidden="true"></i> eSign
                                </span>
                              ) : (
                                <div style={SENT_DOCS_STYLES.fileSubtext}>{item.docModifiedName}</div>
                              )
                            )}
                          </td>
                          <td style={SENT_DOCS_STYLES.cell}>
                            <span style={SENT_DOCS_STYLES.typeBadge}>{getDocType(item.uploadType)}</span>
                          </td>
                          <td style={SENT_DOCS_STYLES.cell}>{item.docSize || "-"}</td>
                          <td style={SENT_DOCS_STYLES.cell}>
                            <div style={SENT_DOCS_STYLES.notesText}>{item.notes || "No notes added."}</div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div style={SENT_DOCS_STYLES.sectionCard}>
              <div style={SENT_DOCS_STYLES.sectionHeader}>
                <div style={SENT_DOCS_STYLES.sectionTitleWrap}>
                  <div style={SENT_DOCS_STYLES.sectionIcon}>
                    <i className="fa fa-download" aria-hidden="true"></i>
                  </div>
                  <div>
                    <div style={SENT_DOCS_STYLES.sectionTitle}>Received Documents</div>
                    <div style={SENT_DOCS_STYLES.sectionMeta}>Files returned or shared back into your workflow.</div>
                  </div>
                </div>
                <div style={SENT_DOCS_STYLES.sectionBadge}>{receivedDocs.length}</div>
              </div>
              <div className="table-responsive">
                <table className="table align-items-center table-hover mb-0" style={SENT_DOCS_STYLES.table}>
                  <thead>
                    <tr>
                      <th style={SENT_DOCS_STYLES.tableHeadCell}>Date & Time Received</th>
                      <th style={SENT_DOCS_STYLES.tableHeadCell}>Name</th>
                      <th style={SENT_DOCS_STYLES.tableHeadCell}>Type</th>
                      <th style={SENT_DOCS_STYLES.tableHeadCell}>Size</th>
                      <th style={SENT_DOCS_STYLES.tableHeadCell}>Notes</th>
                    </tr>
                  </thead>
                  <tbody style={{ opacity: isDataFetching && receivedDocs.length > 0 ? 0.7 : 1, transition: "opacity 0.2s ease" }}>
                    {isDataLoading ? (
                      <tr>
                        <td colSpan="5" style={{ padding: "48px 20px", textAlign: "center", backgroundColor: "#ffffff" }}>
                          <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: "10px" }}>
                            <div style={{ width: "40px", height: "40px", background: "#f5f5f5", display: "flex", alignItems: "center", justifyContent: "center", color: "#000000", fontSize: "16px" }}>
                              <i className="fa fa-circle-o-notch fa-spin"></i>
                            </div>
                            <div style={{ fontSize: "14px", fontWeight: 600, color: "#0f172a" }}>
                              {isDataFetching ? "Fetching received documents..." : "Loading received documents..."}
                            </div>
                            <div style={{ fontSize: "12px", fontWeight: 500, color: "#64748b" }}>
                              Please wait while we retrieve your received files.
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : receivedDocs.length === 0 ? (
                      <tr>
                        <td colSpan="5" style={{ ...SENT_DOCS_STYLES.cell, padding: 0 }}>
                          <div style={SENT_DOCS_STYLES.emptyState}>
                            <div style={SENT_DOCS_STYLES.emptyIcon}>
                              <i className="fa fa-download" aria-hidden="true"></i>
                            </div>
                            <h4 style={SENT_DOCS_STYLES.emptyTitle}>No received documents yet</h4>
                            <p style={SENT_DOCS_STYLES.emptyText}>
                              Received documents will show up here once files are shared back into your workflow.
                            </p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      receivedDocs.map((item, key) => (
                        <tr key={item.id || `received-${key}`}>
                          <td style={SENT_DOCS_STYLES.cell}>
                            <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                              <span style={{ fontSize: "13.5px", fontWeight: 600, color: "#0f172a" }}>
                                <Moment format="MMM Do, YYYY">
                                  {item.createdDateTime}
                                </Moment>
                              </span>
                              <span style={{ fontSize: "12px", fontWeight: 600, color: "#64748b" }}>
                                <Moment format="h:mm a">
                                  {item.createdDateTime}
                                </Moment>
                              </span>
                            </div>
                          </td>
                          <td style={SENT_DOCS_STYLES.cell}>
                            <button
                              className="btn btn-sm p-0 doc-link-btn"
                              style={SENT_DOCS_STYLES.fileLink}
                              title="Download Document"
                              disabled={downloadingDocId === (item.docId || item.id)}
                              onClick={() => handleDownloadFile(item)}
                            >
                              {downloadingDocId === (item.docId || item.id) ? (
                                <i className="fa fa-refresh fa-spin mr-1" aria-hidden="true"></i>
                              ) : (
                                <i className="fa fa-file-text-o mr-1" aria-hidden="true"></i>
                              )}
                              {(item.docId || item.id) ? `${item.docId || item.id} - ` : ""}{item.documentName}
                            </button>
                            {item.docModifiedName && (
                              String(item.docModifiedName).toLowerCase() === "esign" ? (
                                <span style={SENT_DOCS_STYLES.esignBadge}>
                                  <i className="fa fa-pencil-square-o" aria-hidden="true"></i> eSign
                                </span>
                              ) : (
                                <div style={SENT_DOCS_STYLES.fileSubtext}>{item.docModifiedName}</div>
                              )
                            )}
                          </td>
                          <td style={SENT_DOCS_STYLES.cell}>
                            <span style={SENT_DOCS_STYLES.typeBadge}>{getDocType(item.uploadType || item.docType)}</span>
                          </td>
                          <td style={SENT_DOCS_STYLES.cell}>{item.docSize || "-"}</td>
                          <td style={SENT_DOCS_STYLES.cell}>
                            <div style={SENT_DOCS_STYLES.notesText}>{item.notes || "No notes added."}</div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {uploadDocShowHide &&

        <UploadDocPopup
          uploadDocShowHide={uploadDocShowHide}
          handleUploadDocModalShowHide={handleUploadDocModalShowHide}
          runningApplication={wfTask}
        ></UploadDocPopup>}
    </React.Fragment>
  );
}
